import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { pop, reduced } from './motion';

/** Раскладка по столбцам — та же арифметика, что в masPut прототипа (engine.ts:618-623): очередная
    карточка идёт в столбец, который сейчас короче, при равной высоте — в первый; зазор прибавляется
    только со второй карточки столбца (`H[c] += (N[c] ? gp : 0) + hs[i]`), а не к каждой.
    Чистая функция: её одинаково считают и замер, и расчёт для скрытой кладки.
    → столбец каждой карточки и итог: высоты столбцов и число карточек в них */
function layout(heights: number[], gap: number): { target: number[]; sums: number[]; counts: number[] } {
  const sums = [0, 0];
  const counts = [0, 0];
  const target = heights.map(height => {
    const column = Math.round(sums[0]) <= Math.round(sums[1]) ? 0 : 1;
    sums[column] += (counts[column] ? gap : 0) + height;
    counts[column]++;
    return column;
  });
  return { target, sums, counts };
}
/** С какой разницы столбцов в короткий встаёт карточка-продолжение (masonry эталона: gap >= 96) */
const MORE_MIN = 96;

/** Держатели карточек: React рисует карточку порталом в её держатель, а столбец меняет сам держатель.
    Так плитка, ушедшая в другой столбец, остаётся тем же узлом (картинка, видео и переход выдачи не
    начинаются заново). display: contents — для столбца (.c — flex с зазором) карточка остаётся прямым участником. */
class Holders {
  private nodes = new Map<string, HTMLElement>();
  get(key: string): HTMLElement {
    let node = this.nodes.get(key);
    if (!node) {
      node = document.createElement('div');
      node.style.display = 'contents';
      this.nodes.set(key, node);
    }
    return node;
  }
  /** Держатели ушедших карточек снимаются с разметки */
  keep(keys: Set<string>) {
    for (const [key, node] of this.nodes)
      if (!keys.has(key)) {
        node.remove();
        this.nodes.delete(key);
      }
  }
}

/** Кладка в два столбца, как masonry/masPut прототипа: одна раскладка до отрисовки кадра.
    Плитки встают в разметку, их высоты меряются одним проходом, затем каждая уходит в свой столбец.
    Высота берётся измеренная, а не расчётная: у стопки подборки есть ещё колода позади, и предсказанная
    высота части карточек меньше настоящей. Меряется отношение высоты к ширине — трансформ входа
    (scale) на карточке сокращается, и замер равен вёрсточному. Расчёт по соотношению сторон (плюс
    extraHeight) остаётся только для скрытой кладки, где мерить нечего. */
export function Masonry<T>({
  items,
  itemKey,
  aspectRatio,
  extraHeight,
  enterKey,
  render,
  more,
  onMore,
}: {
  items: T[];
  itemKey(item: T): string;
  aspectRatio(item: T): number;
  /** Постоянная добавка к расчётной высоте, px (колода позади стопки) */
  extraHeight?(item: T): number;
  /** Смена значения — вся кладка проявляется заново (pop прототипа: формат ленты главной, setCat) */
  enterKey?: string;
  render(item: T): ReactNode;
  /** Карточка-продолжение: встаёт в конец короткого столбца высотой ровно в разницу, если столбцы разошлись хотя бы
      на 96 px, и низ выравнивается (masonry(m, items, more) эталона; у ленты главной — «Смотреть все»). Высоту ставит
      раскладка на самом элементе карточки — как у эталона, без лишней перерисовки */
  more?: ReactNode;
  /** Встала ли карточка-продолжение: у эталона от этого зависит кнопка под кладкой (#stAll.hidden) */
  onMore?(shown: boolean): void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [holders] = useState(() => new Holders());
  /* держатель карточки-продолжения: стоит в конце короткого столбца, когда она нужна, иначе вне разметки */
  const [moreHolder] = useState(() => new Holders());
  const report = useRef(onMore);
  useLayoutEffect(() => {
    report.current = onMore;
  });
  const place = useRef(() => {});
  const placed = useRef({ keys: '', width: -1 });
  const entered = useRef(enterKey);

  useLayoutEffect(() => {
    place.current = () => {
      const element = root.current;
      const columns = element ? ([...element.children].filter(node => node.classList.contains('c')) as HTMLElement[]) : [];
      if (!element || columns.length < 2) return;
      const keys = items.map(itemKey);
      holders.keep(new Set(keys));
      const nodes = keys.map(key => holders.get(key));
      // новые держатели встают в первый столбец: так их можно измерить вместе со всеми (masPut)
      nodes.forEach(node => {
        if (!node.parentElement) columns[0].appendChild(node);
      });
      const visible = !!columns[0].getClientRects().length;
      const width = visible ? columns[0].getBoundingClientRect().width : 0;
      const signature = keys.join('|');
      if (placed.current.keys === signature && Math.abs(placed.current.width - width) < 0.5) return;
      placed.current = { keys: signature, width };
      const gap = parseFloat(getComputedStyle(columns[0]).rowGap) || 0;
      const estimate = Math.max(1, width || (360 - gap) / 2);
      const heights = items.map((item, index) => {
        const card = nodes[index].firstElementChild;
        const rect = visible ? card?.getBoundingClientRect() : undefined;
        return rect?.width ? rect.height * (width / rect.width) : estimate / aspectRatio(item) + (extraHeight?.(item) ?? 0);
      });
      const { target, sums, counts } = layout(heights, gap);
      const tail = moreHolder.get('more');
      tail.remove();
      // перестановка только там, где порядок разошёлся: держатель на месте не трогается
      [0, 1].forEach(index => {
        const column = columns[index];
        let cursor = column.firstChild;
        nodes.forEach((node, position) => {
          if (target[position] !== index) return;
          if (node === cursor) cursor = cursor.nextSibling;
          else column.insertBefore(node, cursor);
        });
      });
      /* Низ кладки (masonry эталона): разница столбцов в целых px минус зазор над карточкой; скрытой кладке
         мерить нечего — карточки нет, как у эталона */
      const h0 = Math.round(sums[0]),
        h1 = Math.round(sums[1]),
        short = h0 <= h1 ? 0 : 1,
        rest = Math.abs(h0 - h1) - (counts[short] ? gap : 0),
        card = more && visible && rest >= MORE_MIN ? rest : null;
      if (card !== null) {
        (tail.firstElementChild as HTMLElement | null)?.style.setProperty('height', `${card}px`);
        columns[short].appendChild(tail);
      }
      report.current?.(card !== null);
    };
    place.current();
    if (entered.current !== enterKey) {
      entered.current = enterKey;
      if (!reduced()) pop(root.current);
    }
  });

  useLayoutEffect(() => {
    const element = root.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    /* Ширина столбца сменилась (поворот, окно, кладка показалась из-под скрытого блока) — раскладка заново, без анимации.
       В следующем кадре: перекладка меняет высоту самой кладки, и внутри обратного вызова наблюдатель зациклился бы */
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => place.current());
    });
    observer.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={root} className="st-mas">
      <div className="c" />
      <div className="c" />
      {items.map(item => createPortal(render(item), holders.get(itemKey(item)), itemKey(item)))}
      {more && createPortal(more, moreHolder.get('more'), 'more')}
    </div>
  );
}
